import {
  DATA_CLICK_AWAY_ID,
  DATA_CLICK_AWAY_PROPS,
  DomNodes,
  INITIAL_DROP_ATTRIBUTES,
  SORTABLE_DROP_ATTRIBUTES,
  scrollBarMixin
} from "../../constants"
import { DndContext, DragStartEvent } from "@dnd-kit/core"
import {
  rectSortingStrategy,
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { Box, styled } from "@mui/material"
import { FC, useCallback, useMemo, useState, useEffect, memo } from "react"
import {
  generateAnnouncement,
  generateHumanReadableIds,
  useFindNodes,
  useOutsideClick,
  useTestPartFinishedCheck
} from "../../helpers"
import { BlockDropContainerComponent } from "../../ui/containers/BlockDropContainer"
import {
  BasicDragContainerComponent,
  DragContainerComponent
} from "../../ui/containers/DragContainer"
import { DragOverlayContainerComponent } from "../../ui/containers/DragOverlayContainer"
import {
  SortableContainerComponent,
  SortingButtons,
  TransferButton
} from "./components"
import {
  ContainersId,
  ManualSortEnum,
  ManualSortType,
  MoveChoiceEnum,
  MoveChoiceType,
  OrientationEnum,
  OrientationType
} from "./types"
import {
  useContainersStateControl,
  useDragDropChoiceControl,
  useSaveStudentAnswer
} from "./hooks"
import { getChoiceIndex, getListChoicesIds } from "./utils"
import {
  isQuestionComplete,
  NeutralSetting,
  onSaveStudentAnswer
} from "src/types"

interface OrderInteractionProps {
  itemId: string
  responseidentifier: string
  shuffle?: string
  minchoices?: string
  maxchoices?: string
  orientation?: OrientationType
  children: JSX.Element
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  isCompletedQuestion?: isQuestionComplete
  isNeutralSetting?: NeutralSetting
}

export const OrderInteractionComponent: FC<OrderInteractionProps> = memo(
  props => {
    const {
      responseidentifier: responseIdentifier,
      shuffle,
      orientation,
      minchoices: minChoices,
      maxchoices: maxChoices,
      children,
      accessibilityAttr,
      onSaveStudentAnswer,
      isCompletedQuestion,
      isNeutralSetting
    } = props

    const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>()
    const [removingChoiceId, setRemovingChoiceId] = useState<string | null>(
      null
    )
    const { questionCompleted, correctAnswers } = isCompletedQuestion?.() ?? {
      questionCompleted: false,
      correctAnswers: []
    }

    const [[prompt], simpleChoices] = useFindNodes(children, [
      DomNodes.prompt,
      DomNodes.simpleChoice
    ])

    const [initialChoices, sortableChoices, setSortableChoices, moveChoice] =
      useContainersStateControl(
        responseIdentifier,
        simpleChoices,
        shuffle,
        Number(minChoices),
        Number(maxChoices),
        questionCompleted,
        isNeutralSetting ? undefined : correctAnswers
      )
    const [
      activeId,
      handleDragStartFromHook,
      handleDragEnd,
      handleDragOver,
      sortingChoices,
      sensors,
      customCollisionDetectionAlgorithm,
      handleDragMove,
      handleCancelDrag
    ] = useDragDropChoiceControl(
      initialChoices,
      sortableChoices,
      setSortableChoices,
      moveChoice
    )

    // Clear selection when starting to drag a different item from initial container
    const handleDragStart = useCallback(
      (e: DragStartEvent) => {
        const draggedId = `${e.active.id}`
        // Check if the dragged item is in the initial container
        const isDraggedFromInitial = initialChoices?.some(
          choice => choice.id === draggedId
        )

        // Only clear selection if dragging a different item from the initial container
        if (
          isDraggedFromInitial &&
          selectedChoiceId &&
          selectedChoiceId !== draggedId
        ) {
          setSelectedChoiceId(null)
        }
        handleDragStartFromHook(e)
      },
      [initialChoices, selectedChoiceId, handleDragStartFromHook]
    )

    // Clear removingChoiceId when sortableChoices updates
    useEffect(() => {
      if (
        removingChoiceId &&
        !sortableChoices?.some(c => c?.id === removingChoiceId)
      ) {
        setRemovingChoiceId(null)
      }
    }, [sortableChoices, removingChoiceId])

    // Only pass selectedChoiceId for feedback if it's from the "draggable" container
    const isSelectedInInitial =
      selectedChoiceId &&
      initialChoices?.some(choice => choice.id === selectedChoiceId)

    const { answerRevealedSelectedChoices } = useSaveStudentAnswer(
      isSelectedInInitial ? selectedChoiceId : null,
      activeId,
      sortableChoices,
      Number(minChoices) || 1, // if min = 0, use 1 to give user 1 attempt before answ reveal
      Number(maxChoices) || 1, // if max = 0, use 1 to give user 1 attempt before answ reveal
      onSaveStudentAnswer,
      questionCompleted,
      removingChoiceId
    )

    const isFinished = useTestPartFinishedCheck()

    const selectItem = useCallback(
      (id: string) => () =>
        setSelectedChoiceId(state => (state === id ? null : id)),
      []
    )

    const manualSort = useCallback(
      (direction: ManualSortType) => {
        if (isFinished || !sortableChoices) return

        const dragIndex = getChoiceIndex(
          getListChoicesIds(sortableChoices),
          selectedChoiceId
        )
        return () => {
          const dropIndex = dragIndex - direction
          if (dropIndex < 0 || dropIndex > sortableChoices.length - 1) {
            return
          }
          const dropId = sortableChoices[dragIndex - direction].id
          sortingChoices(selectedChoiceId, dropId)
        }
      },
      [isFinished, sortableChoices, selectedChoiceId, sortingChoices]
    )

    const transferChoiceDirection: MoveChoiceType = useMemo(() => {
      if (!initialChoices || !sortableChoices) return
      const initialChoiceIds = getListChoicesIds(initialChoices)
      const sortableChoiceIds = getListChoicesIds(sortableChoices)

      return (
        (initialChoiceIds.includes(selectedChoiceId) &&
          MoveChoiceEnum.fromInit) ||
        (sortableChoiceIds.includes(selectedChoiceId) && MoveChoiceEnum.toInit)
      )
    }, [initialChoices, sortableChoices, selectedChoiceId])

    const clickTransferButtonHandler = useCallback(() => {
      moveChoice(transferChoiceDirection)(selectedChoiceId)
    }, [transferChoiceDirection, selectedChoiceId, moveChoice])

    const handleRemove = useCallback(
      (id: string) => () => {
        // Set removing state before any other state changes
        setRemovingChoiceId(id)
        // Clear selection to prevent it from being used
        if (selectedChoiceId === id) {
          setSelectedChoiceId(null)
        }
        moveChoice(MoveChoiceEnum.toInit)(id)
      },
      [selectedChoiceId, moveChoice]
    )

    const announcements = useMemo(
      () =>
        generateAnnouncement(
          generateHumanReadableIds(simpleChoices),
          false,
          false,
          [
            [ContainersId.INITIAL_DROP, "Initial container"],
            [ContainersId.SORTABLE_DROP, "Sortable container"]
          ]
        ),
      [simpleChoices]
    )

    const clickOutsideCallback = useCallback(
      () => setSelectedChoiceId(null),
      []
    )

    useOutsideClick(`[${DATA_CLICK_AWAY_ID}]`, clickOutsideCallback)
    return (
      <>
        {prompt}
        <OrderInteractionWrapper
          orientation={orientation}
          {...accessibilityAttr}
        >
          <DndContext
            sensors={sensors}
            onDragMove={handleDragMove}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragCancel={handleCancelDrag}
            collisionDetection={customCollisionDetectionAlgorithm}
            accessibility={{ announcements }}
          >
            <BlockDropContainerComponent
              id={ContainersId.INITIAL_DROP}
              orientation={orientation}
              {...INITIAL_DROP_ATTRIBUTES}
            >
              {initialChoices &&
                initialChoices.map(choice => {
                  const id = choice?.id
                  return (
                    <DragContainerComponent
                      key={id}
                      id={id}
                      {...DATA_CLICK_AWAY_PROPS}
                      drag={activeId === id}
                      placehold={activeId === id}
                      selected={selectedChoiceId === id}
                      onClick={selectItem(id)}
                      disabled={isFinished || questionCompleted}
                      isNeutralSetting={isNeutralSetting}
                    >
                      {choice?.element.props.children}
                    </DragContainerComponent>
                  )
                })}
            </BlockDropContainerComponent>

            <TransferButton
              disabled={!Boolean(selectedChoiceId) || questionCompleted}
              orientation={orientation}
              direction={transferChoiceDirection}
              clickHandler={clickTransferButtonHandler}
            />

            <BlockDropContainerComponent
              id={ContainersId.SORTABLE_DROP}
              orientation={orientation}
              {...SORTABLE_DROP_ATTRIBUTES}
            >
              {sortableChoices && (
                <SortableContext
                  items={sortableChoices}
                  strategy={
                    orientation === OrientationEnum.vertical
                      ? verticalListSortingStrategy
                      : rectSortingStrategy
                  }
                >
                  {sortableChoices.map((choice, i) => {
                    const id = choice?.id
                    const isCorrect = answerRevealedSelectedChoices
                      ? answerRevealedSelectedChoices.get(id)
                      : null
                    return (
                      <SortableContainerComponent
                        key={id}
                        id={id}
                        {...DATA_CLICK_AWAY_PROPS}
                        selected={selectedChoiceId === id}
                        drag={activeId === id}
                        onClick={selectItem(id)}
                        handleRemove={handleRemove(id)}
                        index={
                          orientation === OrientationEnum.vertical
                            ? i + 1
                            : null
                        }
                        disabled={isFinished || questionCompleted}
                        isCorrect={isCorrect}
                        isNeutralSetting={isNeutralSetting}
                      >
                        {choice?.element.props.children}
                      </SortableContainerComponent>
                    )
                  })}
                </SortableContext>
              )}
            </BlockDropContainerComponent>

            <SortingButtons
              disabled={
                !sortableChoices?.some(
                  choice => choice.id === selectedChoiceId
                ) ||
                sortableChoices.length < 2 ||
                questionCompleted
              }
              sortableChoices={sortableChoices}
              selectedChoiceId={selectedChoiceId}
              orientation={orientation}
              clickHandlerUp={manualSort(ManualSortEnum.up)}
              clickHandlerDown={manualSort(ManualSortEnum.down)}
            />

            <DragOverlayContainerComponent dropAnimation={null}>
              {activeId && (
                <BasicDragContainerComponent
                  placehold
                  isNeutralSetting={isNeutralSetting}
                >
                  {
                    simpleChoices.find(
                      choice => choice.props.identifier === activeId
                    ).props.children
                  }
                </BasicDragContainerComponent>
              )}
            </DragOverlayContainerComponent>
          </DndContext>
        </OrderInteractionWrapper>
      </>
    )
  }
)

interface OrderInteractionWrapperProps {
  orientation: OrientationType
}

const OrderInteractionWrapper = styled(Box)<OrderInteractionWrapperProps>(
  ({ theme, orientation }) => `
  display: flex;
  flex-direction: ${
    orientation === OrientationEnum.vertical ? "row" : "column"
  };
  margin: ${theme.spacing(4, 0, 2)};
  padding-bottom: ${theme.spacing(3.5)};
  max-width: 100%;
  width: 100%;
  ${
    orientation === OrientationEnum.vertical
      ? `
    overflow-x: auto;
    ${scrollBarMixin(theme, "horizontal")};
  `
      : ""
  }
`
)
